import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {getEcaFields, getFieldGridCSVData} from '../../../api/EcaServices';
import {ReactComponent as BackgroundBlur} from '../../../assests/svg/backgroundBlur.svg';
import {
  EciHomePageTabs,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import {setModalStatus} from '../../../redux/slices/invoiceModalSlice';
import {RootState} from '../../../redux/store';
import {getProjectReportFileName, isEmpty} from '../../../utils/utils';
import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import AurButton from '../../common/aur-button/AurButton';
import {
  AurDropdownButton,
  DropdownIconTypes,
} from '../../common/aur-button/AurDropdownButton';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {CustomFieldDrawer} from './custom-field/CustomFieldDrawer';
import {IFieldModal} from './custom-field/constant';
import FieldTableContainer from './field-table/FieldTableContainer';
import {FieldCategoryType} from './custom-field/FieldView';
import {GridSortModel} from '@mui/x-data-grid';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import {
  PAGE_TYPE,
  useManageMenuByPermissions,
} from '../../../hooks/permissions/useManageMenuByPermissions';
import ProjectSwitcherModal from '../../ecapages/eca-overview/eca-overlay/ProjectSwitcherModal';
import {ReactComponent as CloseIconLight} from '../../../assests/svg/close_icon_light.svg';

export interface FieldFilter {
  fieldCategory: FieldCategoryType[];
}

const FieldManagement: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {manageMenus} = useManageMenuByPermissions(PAGE_TYPE.Field);

  const breadCrumbItems = [
    {
      label: t('eca.ecaHomepage.coreEciHome'),
      path: `/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`,
    },
    {
      label: t('fieldManagement.manageFields'),
    },
  ];

  const [reqLoading, setReqLoading] = useState<boolean>(false);
  const [haveReqError, setHaveReqError] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const {
    hasAuditReportsPerm,
    hasCreateOrEditTagFieldsPerm,
    hasCreateOrEditCustomFieldsPerm,
  } = usePermissionsByPage(PAGE_NAME.FIELD_MANAGEMENT);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'updatedOn',
      sort: 'desc',
    },
  ]);

  const [ecaFieldArray, setEcaFieldArray] = useState<IFieldModal[]>([]);
  const currentAPIParams = useRef<string>('');

  const [selectedFieldRowIndex, setSelectedFieldRowIndex] = useState<
    number | undefined
  >();
  const filterSortParam = useRef<string>();

  const [showCreateCustomTagField, setShowCreateCustomTagField] =
    useState(false);

  const [showCreateCustomMetaDataField, setShowCreateCustomMetaDataField] =
    useState(false);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const {selectedProjectName, projectId} = useSelector(
    (state: RootState) => state.reports
  );

  const exportGridData = useCallback(async () => {
    if (clientId && projectId) {
      setIsDownloading(true);
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      try {
        const response = await getFieldGridCSVData(params);
        const blob = new Blob([response], {type: 'text/csv'});
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute(
          'download',
          `${getProjectReportFileName(selectedProjectName, t('fieldManagement.manageFields'))}.csv`
        );
        document.body.appendChild(link);
        link.click();
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error('Error exporting CSV:', error);
      } finally {
        setIsDownloading(false);
      }
    }
  }, [clientId, projectId]);

  const fetchEcaFields = useCallback(async () => {
    try {
      const filterSortParamString = !isEmpty(filterSortParam.current)
        ? `&${filterSortParam.current}`
        : '';

      const params = `clientId=${clientId}&projectId=${projectId}&skip=${itemsPerPage * (currentPage - 1)}&top=${itemsPerPage}${filterSortParamString}`;

      currentAPIParams.current = params;

      setReqLoading(true);

      let ecaFields: IFieldModal[] = [];
      const result = await getEcaFields(params);

      const totalCount = result?.data?.meta?.pagination?.totalItems ?? 0;
      const resultData: IFieldModal[] = result?.data?.data ?? [];

      ecaFields = resultData.map((group: IFieldModal, index: number) => {
        if (
          isEmpty(group.createdBy) &&
          !isEmpty(group.createdOn) &&
          group.fieldCategory == 'STANDARD_METADATA'
        ) {
          group.createdBy = 'ecasystem';
        }
        if (
          isEmpty(group.updatedBy) &&
          !isEmpty(group.updatedOn) &&
          group.fieldCategory == 'CUSTOM_METADATA'
        ) {
          group.updatedBy = 'ecasystem';
        }

        if (
          isEmpty(group.updatedBy) &&
          !isEmpty(group.updatedOn) &&
          !isEmpty(group.createdBy)
        ) {
          group.updatedBy = group.createdBy;
        }
        return {
          ...group,
          index: index + 1,
        };
      });

      setTotalItems(totalCount);
      setEcaFieldArray([...ecaFields]);
      setReqLoading(false);
    } catch (error) {
      console.error('Failed to fetch ECA fields:', error);
      currentAPIParams.current = '';
      setHaveReqError(true);
    } finally {
      setReqLoading(false);
    }
  }, [clientId, currentPage, itemsPerPage, projectId]);

  useEffect(() => {
    if (filterSortParam.current) {
      fetchEcaFields();
    }
  }, [currentPage, fetchEcaFields, itemsPerPage]);

  useEffect(() => {
    setTotalItems(0);
    setCurrentPage(1);
    setEcaFieldArray([]);
  }, [projectId, clientId]);

  const onClickProjectButton = useCallback(() => {
    dispatch(setModalStatus(true));
  }, [dispatch]);

  const debouncedFetchEcaFields = useMemo(
    () => debounce(fetchEcaFields, 2000),
    [fetchEcaFields]
  );

  const onChangeFilterSortParams = useCallback(
    (_filterSortParam: string) => {
      if (filterSortParam.current !== _filterSortParam) {
        filterSortParam.current = _filterSortParam ?? '';
        if (currentPage === 1) {
          debouncedFetchEcaFields();
        } else {
          setCurrentPage(1);
        }
      }
    },
    [debouncedFetchEcaFields, currentPage]
  );

  const onSetSortModel = (sortModel: GridSortModel) => {
    const filteredSortModel = sortModel?.filter((model) => model.sort);
    setSortModel(filteredSortModel);
  };

  const handleOnCloseCustomFieldDrawer = useCallback(
    (isRefresh: boolean, type?: FieldCategoryType) => {
      if (type === 'TAG_FIELD') {
        setShowCreateCustomTagField(false);
      } else if (type === 'CUSTOM_METADATA') {
        setShowCreateCustomMetaDataField(false);
      } else {
        setSelectedFieldRowIndex(undefined);
      }
      if (isRefresh) {
        fetchEcaFields();
      }
    },
    [fetchEcaFields]
  );

  const menus = useMemo(() => {
    const items = [];
    if (hasCreateOrEditTagFieldsPerm)
      items.push({
        label: t('fieldManagement.newFieldMenu.customTagField'),
        onClick: () => {
          setShowCreateCustomTagField(true);
        },
      });

    if (hasCreateOrEditCustomFieldsPerm)
      items.push({
        label: t('fieldManagement.newFieldMenu.customMetadataField'),
        onClick: () => {
          setShowCreateCustomMetaDataField(true);
        },
      });

    return items;
  }, [hasCreateOrEditCustomFieldsPerm, hasCreateOrEditTagFieldsPerm]);

  return (
    <>
      <Box className="aur-field-management">
        <BackgroundBlur className={'aur-field-management-bg-image'} aria-hidden="true" />
        <Box className={'header-content'}>
          <Box className={'header-top-content'}>
            <Box
              className={'aur-breadcrumb-container'}
              display={'flex'}
              justifyContent={'space-between'}
            >
              <AurBreadcrumb items={breadCrumbItems} />
              {hasAuditReportsPerm && (
                <AurButton
                  dataTestId={'viewTaggingAudit'}
                  classNames={'typo-text buttonMedium_size view-report-button'}
                  type={'text'}
                  size={'Medium'}
                  endIcon={
                    <AurIcon
                      icon={IconNames.ChevronRight}
                      color={IconColors.Custom}
                      customClassName={'manage-fields-dropdown-icon'}
                    />
                  }
                  ariaLabel={t('fieldManagement.viewTaggingAuditReport')}
                  label={t('fieldManagement.viewTaggingAuditReport')}
                  onClick={() => navigate('/eci-home-page/tagging-report')}
                />
              )}
            </Box>
            <Box className={'field-management-project-name-container'}>
              <Typography
                noWrap
                tabIndex={0}
                className={
                  'typo-text subhead3_size field-management-project-name'
                }
              >
                {selectedProjectName}
              </Typography>
              <Box className={'field-management-manage-fields-container '}>
                <Box className={'field-management-manage-fields'}>
                  <Typography className={'typo-text display3_size'} tabIndex={0}>
                    {t('fieldManagement.manageFields')}
                  </Typography>
                </Box>
                <Box
                  className={'field-management-manage-fields-buttons-container'}
                >
                  {menus.length > 0 && (
                    <AurDropdownButton
                      elementID={'newFieldButton'}
                      buttonText={t('fieldManagement.newField')}
                      type={'continue'}
                      hintText={t('fieldManagement.newFieldHint')}
                      dropdownRole="button"
                      startIcon={
                        <AurIcon
                          icon={IconNames.Add}
                          color={IconColors.Custom}
                          customClassName={'manage-fields-dropdown-icon'}
                        />
                      }
                      aria-live={'polite'}
                      size={'Medium'}
                      iconType={'Other'}
                      iconSize={'medium'}
                      iconClassName={'manage-fields-dropdown-icon'}
                      menuDirectionLeft={false}
                      menuItemHeight={'medium'}
                      menuItemWidth={'mediumLarge'}
                      menuItemTextSize={'small'}
                      menuItems={menus}
                    />
                  )}
                  {manageMenus?.length > 0 && (
                    <AurDropdownButton
                      elementID={'manageButton'}
                      buttonText={t('fieldManagement.manage')}
                      type={'continue'}
                      aria-live={'polite'}
                      size={'Medium'}
                      iconType={'Arrow'}
                      iconSize={'medium'}
                      iconClassName={'manage-fields-dropdown-icon'}
                      hintText={t('fieldManagement.manageHint')}
                      dropdownRole="button"
                      menuDirectionLeft={false}
                      menuItemHeight={'medium'}
                      menuItemWidth={'large'}
                      menuItemTextSize={'small'}
                      dropDownIconType={DropdownIconTypes.Chev}
                      menuItems={manageMenus}
                    >
                      <AurIcon
                        icon={IconNames.ChevronDown}
                        color={IconColors.Custom}
                        customClassName={'manage-fields-dropdown-icon'}
                      />
                    </AurDropdownButton>
                  )}
                  <AurButton
                    dataTestId={'openProjectButton'}
                    classNames={'typo-text buttonMedium_size'}
                    type={'continue'}
                    size={'Medium'}
                    startIcon={
                      <AurIcon
                        icon={IconNames.SwapHorizOutlined}
                        color={IconColors.Custom}
                        customClassName={'manage-fields-dropdown-icon'}
                      />
                    }
                    label={t('fieldManagement.project')}
                    onClick={onClickProjectButton}
                    ariaLabel={`${t('fieldManagement.project')} ${t('fieldManagement.projectHint')}`}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          {hasCreateOrEditTagFieldsPerm === undefined ||
          hasCreateOrEditCustomFieldsPerm === undefined ? (
            <AurLoadingIndicator
              overlay={true}
              isOpen={
                hasCreateOrEditTagFieldsPerm === undefined ||
                hasCreateOrEditCustomFieldsPerm === undefined
              }
            />
          ) : (
            <FieldTableContainer
              data={ecaFieldArray}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              isReqLoading={reqLoading}
              haveReqError={haveReqError}
              onErrorRefresh={fetchEcaFields}
              onExportGridData={exportGridData}
              isDownloading={isDownloading}
              onChangeOdataParam={onChangeFilterSortParams}
              onPageChange={(pageNumber: number) => {
                setCurrentPage(pageNumber);
              }}
              onSetSortModel={onSetSortModel}
              sortModel={sortModel}
              setItemsPerPage={(pageSize: number) => {
                setCurrentPage(1);
                setItemsPerPage(pageSize);
              }}
              onClickTableRow={(selectedField: IFieldModal) => {
                setSelectedFieldRowIndex(
                  ecaFieldArray.findIndex(
                    (item) => item.fieldId === selectedField.fieldId
                  )
                );
              }}
            />
          )}
        </Box>
        <ProjectSwitcherModal
          className="eca-project-switcher-dialog"
          headingClass="heading-label"
          title={t('fieldManagement.chooseYourProject')}
          heading={t('fieldManagement.manageFields')}
          initialSelectedProjectId={projectId ?? undefined}
          closeIcon={<CloseIconLight aria-hidden />}
        />
        {showCreateCustomTagField && (
          <CustomFieldDrawer
            open={showCreateCustomTagField}
            fieldMode="CREATE"
            fieldCategoryType={'TAG_FIELD'}
            onClose={(isRefresh) =>
              handleOnCloseCustomFieldDrawer(isRefresh, 'TAG_FIELD')
            }
          />
        )}
        {showCreateCustomMetaDataField && (
          <CustomFieldDrawer
            open={showCreateCustomMetaDataField}
            fieldMode={'CREATE'}
            headerTitle={t(
              'fieldManagement.customField.addCustomMetaDataField'
            )}
            fieldCategoryType="CUSTOM_METADATA"
            headerClass={'typo-text headline1_size'}
            onClose={(isRefresh) =>
              handleOnCloseCustomFieldDrawer(isRefresh, 'CUSTOM_METADATA')
            }
          />
        )}
        {typeof selectedFieldRowIndex === 'number' &&
          ecaFieldArray?.[selectedFieldRowIndex] && (
            <CustomFieldDrawer
              open={true}
              fieldMode={'VIEW'}
              fieldCategoryType={
                ecaFieldArray?.[selectedFieldRowIndex].fieldCategory as never
              }
              pagination={{
                initialIndex: selectedFieldRowIndex,
                fieldsData: ecaFieldArray,
              }}
              onClose={(isRefresh) => handleOnCloseCustomFieldDrawer(isRefresh)}
            />
          )}
      </Box>
      <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
    </>
  );
};

export default FieldManagement;
